import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const topLinks = [
  { label: "Building Mgt", to: "/CcpAdminBuildingMgt" },
  { label: "Course Planning", to: "/CcpAdminCoursePlanning" },
  { label: "Courses", to: "/CcpAdminCourses" },
  { label: "Course Entries", to: "/admin/course-entries" },
  { label: "Instructor Type", to: "/CcpAdminInstructorType" },
  { label: "Instructors", to: "/CcpAdminInstructors" },
  { label: "Modality", to: "/CcpAdminModality" },
  { label: "Proctors", to: "/CcpAdminProctors" },
];

const termChildren = [
  { label: "Sinclair Terms", to: "/CcpAdminTerm" },
  { label: "HS Terms", to: "/CcpAdminHsTerm" },
];

const courseChildren = [
  { label: "Logs", to: "/CcpAdminCoursePlanning" },
  { label: "Xcsi", to: "/CcpAdminXcsi" },
  { label: "Course Planning Entries", to: "/CcpAdminCoursePlanningEntries" },
  {
    label: "Beta Testing",
    to: "/CcpAdminCoursePlanningBetaTesting",
  },
];

const CcpCpBreadcrumbs = ({ pageName }) => {
  const location = useLocation();
  const initialOpen = termChildren.some((c) => c.to === location.pathname);
  const [termsOpen, setTermsOpen] = useState(initialOpen);

  // Find the active link label based on the current path
  const allLinks = [...topLinks, ...termChildren];
  const activeLink = allLinks.find((item) => location.pathname === item.to);
  const displayName =
    pageName || (activeLink ? activeLink.label : "Course Planning Logs");

  const isChildActive = (to) => location.pathname === to;

  // Determine if we are in the Course Planning area to show its sub-links
  const isCourseAreaActive =
    location.pathname.startsWith("/CcpAdminCoursePlanning") ||
    location.pathname.startsWith("/CcpAdminXcsi") ||
    activeLink?.to === "/CcpAdminCoursePlanning";

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          mt: 4, // increased top margin so breadcrumbs sit below the app bar
          pt: 1,
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            color: "#222",
            textAlign: "center",
            mb: 1,
          }}
        >
          {displayName}
        </Typography>
      </Box>

      {/* Main breadcrumb row */}
      <Box
        sx={{
          width: "100%",
          background: "#f5f5f5",
          py: 1,
          px: 2,
          borderRadius: 3,
          boxShadow: 1,
          display: "flex",
          alignItems: "center",
          mb: 2,
        }}
      >
        {topLinks.map((item, idx) => {
          const isActive = location.pathname === item.to;
          return (
            <React.Fragment key={item.to}>
              <Box
                component={Link}
                to={item.to}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textDecoration: "none",
                  color: isActive ? "#fff" : "#AC1A2F",
                  background: isActive ? "#AC1A2F" : "transparent",
                  fontWeight: isActive ? 700 : 500,
                  fontSize: "1rem",
                  transition: "background 0.2s, color 0.2s",
                  "&:hover": {
                    background: isActive ? "#AC1A2F" : "#e0e0e0",
                    color: isActive ? "#fff" : "#AC1A2F",
                  },
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {item.label}
              </Box>
              <ChevronRightIcon sx={{ color: "#bbb", mx: 0.5 }} />
            </React.Fragment>
          );
        })}

        {/* Terms parent - toggles children */}
        <React.Fragment key="terms-parent">
          <Box
            component="button"
            onClick={() => setTermsOpen((s) => !s)}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              textDecoration: "none",
              color: termsOpen ? "#fff" : "#AC1A2F",
              background: termsOpen ? "#AC1A2F" : "transparent",
              fontWeight: termsOpen ? 700 : 500,
              fontSize: "1rem",
              transition: "background 0.2s, color 0.2s",
              "&:hover": {
                background: termsOpen ? "#AC1A2F" : "#e0e0e0",
                color: termsOpen ? "#fff" : "#AC1A2F",
              },
              display: "flex",
              alignItems: "center",
              border: "none",
              cursor: "pointer",
              mr: 0.5,
            }}
          >
            Terms
          </Box>
          <ChevronRightIcon sx={{ color: "#bbb", mx: 0.5 }} />
        </React.Fragment>
      </Box>

      {/* Sub-links row (under main breadcrumbs) */}
      {isCourseAreaActive && (
        <Box
          sx={{
            width: "100%",
            background: "#fafafa",
            py: 1,
            px: 3,
            borderRadius: 2,
            boxShadow: 0,
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          {courseChildren.map((item, idx) => {
            const isActive = isChildActive(item.to);
            return (
              <React.Fragment key={item.to}>
                <Box
                  component={Link}
                  to={item.to}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    textDecoration: "none",
                    color: isActive ? "#fff" : "#AC1A2F",
                    background: isActive ? "#AC1A2F" : "transparent",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "0.95rem",
                    transition: "background 0.2s, color 0.2s",
                    "&:hover": {
                      background: isActive ? "#AC1A2F" : "#e0e0e0",
                      color: isActive ? "#fff" : "#AC1A2F",
                    },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {item.label}
                </Box>
                {idx < courseChildren.length - 1 && (
                  <ChevronRightIcon sx={{ color: "#ddd", mx: 0.5 }} />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      )}

      {termsOpen && (
        <Box
          sx={{
            width: "100%",
            background: "#fafafa",
            py: 1,
            px: 3,
            borderRadius: 2,
            boxShadow: 0,
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          {termChildren.map((item, idx) => {
            const isActive = isChildActive(item.to);
            return (
              <React.Fragment key={item.to}>
                <Box
                  component={Link}
                  to={item.to}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    textDecoration: "none",
                    color: isActive ? "#fff" : "#AC1A2F",
                    background: isActive ? "#AC1A2F" : "transparent",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "0.95rem",
                    transition: "background 0.2s, color 0.2s",
                    "&:hover": {
                      background: isActive ? "#AC1A2F" : "#e0e0e0",
                      color: isActive ? "#fff" : "#AC1A2F",
                    },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {item.label}
                </Box>
                {idx < termChildren.length - 1 && (
                  <ChevronRightIcon sx={{ color: "#ddd", mx: 0.5 }} />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default CcpCpBreadcrumbs;
